import {
    useEffect,
    useMemo,
    type FC
} from "react";
import type IThemeSwitcherProps from "./type";
import type {
    ThemeSwitcherIconContextType,
    ThemeSwitcherDefaultKey,
    ThemeSwitchVariant
} from "./type";
import {
    THEME_SWITCHER_DEFAULT_KEYS,
    THEME_SWITCHER_ICON_COLORS,
    THEME_SWITCHER_TITLE_KEYS,
    THEME_SWITCHER_ICONS
} from "./stylesheet";
import {
    ThemeSwitcherIconContext
} from "./context";
import {
    ThemeSwitcherTriggerIcon
} from "./icons";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../core/hooks";
import type {
    NCoreUIKitIcon
} from "../../types";
import type {
    LucideIcon
} from "lucide-react-native";
import {
    getSwitcherBorderColor,
    getSwitcherVariants
} from "../../helpers/preference";
import {
    warnOnce
} from "../../utils/development";
import PreferenceChevronIcon from "../../helpers/preference/menu/chevron";
import PreferenceForm from "../../helpers/preference/form";
import Button from "../button";

const getThemeSwitcherIcon = (Icon: LucideIcon): NCoreUIKitIcon => {
    const ThemeSwitcherIcon: NCoreUIKitIcon = ({
        customColor,
        size
    }) => {
        return <Icon
            color={customColor}
            size={size}
        />;
    };

    return ThemeSwitcherIcon;
};

const ThemeSwitcherIcons: Record<ThemeSwitcherDefaultKey, NCoreUIKitIcon> = {
    system: getThemeSwitcherIcon(THEME_SWITCHER_ICONS.system),
    light: getThemeSwitcherIcon(THEME_SWITCHER_ICONS.light),
    dark: getThemeSwitcherIcon(THEME_SWITCHER_ICONS.dark)
};

const ThemeSwitcher: FC<IThemeSwitcherProps> = ({
    isShowTitle: isShowTitleProp,
    isWorkWithHighlightButton,
    isWorkWithNextShowSystem,
    variants: variantsProp,
    accessibilityLabel,
    isDisabled = false,
    customBorderColor,
    isShowNextVariant,
    variant = "tonal",
    type = "neutral",
    customIconColor,
    customTextColor,
    customLocalize,
    form = "cycle",
    customColor,
    customTheme,
    color,
    title,
    ...props
}) => {
    const {
        activeThemePreference,
        themeKeys
    } = NCoreUIKitTheme.useContext();

    const {
        colors
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const defaultVariants: Array<ThemeSwitchVariant> = THEME_SWITCHER_DEFAULT_KEYS.map((themeKey) => ({
        title: localize(THEME_SWITCHER_TITLE_KEYS[themeKey]),
        iconColor: THEME_SWITCHER_ICON_COLORS[themeKey],
        icon: ThemeSwitcherIcons[themeKey],
        themeKey
    }));

    const {
        accessibilityLabel: switcherAccessibilityLabel,
        variants: switcherVariants,
        displayVariant,
        nextKey
    } = getSwitcherVariants<"themeKey", ThemeSwitchVariant>({
        isShowNextVariant: isShowNextVariant ?? isWorkWithNextShowSystem ?? false,
        title: localize("theme-switcher-title"),
        activeKey: activeThemePreference,
        variants: variantsProp,
        keyName: "themeKey",
        keys: [
            "light",
            "dark",
            ...themeKeys,
            "system"
        ],
        defaultVariants,
        localize
    });

    useEffect(() => {
        if(isWorkWithNextShowSystem === undefined) {
            return;
        }

        warnOnce({
            message: "ThemeSwitcher: \"isWorkWithNextShowSystem\" is deprecated and will be removed in the next major version. Use \"isShowNextVariant\" instead."
        });
    }, [
        isWorkWithNextShowSystem
    ]);

    useEffect(() => {
        if(isWorkWithHighlightButton === undefined) {
            return;
        }

        warnOnce({
            message: "ThemeSwitcher: \"isWorkWithHighlightButton\" is deprecated and will be removed in the next major version. The switcher is a tonal neutral Button by default."
        });
    }, [
        isWorkWithHighlightButton
    ]);

    const isShowTitle = isShowTitleProp ?? form !== "cycle";

    const isMenuForm = form === "menu";

    const displayTitle = displayVariant && isShowTitle ? displayVariant.title ?? String(displayVariant.themeKey) : undefined;

    const currentTitle = title ?? displayTitle;

    const buttonVariant = isWorkWithHighlightButton || variant === "filled" ? "tonal" : variant;

    const isTitleInAccessibilityLabel = !currentTitle || switcherAccessibilityLabel.includes(currentTitle);

    const onPress = () => {
        if(!nextKey) {
            return;
        }

        NCoreUIKitTheme.switch({
            themeKey: nextKey
        });
    };

    const handleSelect = (key: string) => {
        const selectedVariant = switcherVariants.find((variantItem) => String(variantItem.themeKey) === key);

        if(!selectedVariant) {
            return;
        }

        NCoreUIKitTheme.switch({
            themeKey: selectedVariant.themeKey
        });
    };

    const displayIconKey = THEME_SWITCHER_DEFAULT_KEYS.find((themeKey) => {
        return displayVariant?.themeKey === themeKey && displayVariant.icon === ThemeSwitcherIcons[themeKey];
    });

    const isCustomIconColor = !!customIconColor || !!customTextColor;

    const iconContextValue = useMemo<ThemeSwitcherIconContextType>(() => ({
        icon: displayIconKey ? THEME_SWITCHER_ICONS[displayIconKey] : undefined,
        variant: displayVariant,
        isCustomIconColor,
        customTheme,
        isDisabled
    }), [
        displayIconKey,
        displayVariant,
        isCustomIconColor,
        customTheme,
        isDisabled
    ]);

    const options = form === "cycle" ? [] : switcherVariants.map((variantItem) => ({
        title: variantItem.title ?? String(variantItem.themeKey),
        key: String(variantItem.themeKey),
        icon: variantItem.icon
    }));

    const trigger = <Button
        {...props}
        customColor={customColor || (displayVariant && displayVariant.backgroundColor ? displayVariant.backgroundColor : color)}
        accessibilityLabel={accessibilityLabel ?? (isTitleInAccessibilityLabel ? switcherAccessibilityLabel : undefined)}
        icon={isMenuForm && currentTitle ? PreferenceChevronIcon : ThemeSwitcherTriggerIcon}
        iconDirection={isMenuForm && currentTitle ? "end" : props.iconDirection}
        customBorderColor={getSwitcherBorderColor({
            borderColor: displayVariant?.borderColor,
            customBorderColor,
            colors,
            color
        })}
        customIconColor={customIconColor}
        customTextColor={customTextColor}
        customTheme={customTheme}
        variant={buttonVariant}
        isDisabled={isDisabled}
        title={currentTitle}
        onPress={onPress}
        type={type}
    />;

    return <ThemeSwitcherIconContext.Provider
        value={iconContextValue}
    >
        <PreferenceForm
            size={props.size === "xSmall" ? "small" : props.size}
            activeKey={String(activeThemePreference)}
            title={localize("theme-switcher-title")}
            spreadBehaviour={props.spreadBehaviour}
            customLocalize={customLocalize}
            customTheme={customTheme}
            isShowTitle={isShowTitle}
            onSelect={handleSelect}
            isDisabled={isDisabled}
            testID={props.testID}
            style={props.style}
            trigger={trigger}
            options={options}
            form={form}
        />
    </ThemeSwitcherIconContext.Provider>;
};
export default ThemeSwitcher;
